import { useEffect, useState } from "react";
import { nodeSchema, type DesignNode } from "@proof/core";
import { Select, Button, Control, Toggle } from "./components";

export function VectorPath({
  node,
  onChange,
}: {
  node: DesignNode;
  onChange: (patch: Partial<DesignNode>) => void;
}) {
  const saved = {
    path: node.path || "",
    width: node.pathWidth || node.width,
    height: node.pathHeight || node.height,
    viewport: node.pathViewport || {
      x: 0,
      y: 0,
      width: node.pathWidth || node.width,
      height: node.pathHeight || node.height,
      yAxis: "down" as const,
    },
    custom: !!node.pathViewport,
  };
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [node.id, JSON.stringify(saved)]);
  const patch = {
    path: draft.path,
    pathWidth: draft.width,
    pathHeight: draft.height,
    pathViewport: draft.custom ? draft.viewport : undefined,
  };
  const validation = nodeSchema.safeParse({ ...node, ...patch });
  return (
    <details className="vector-path-editor">
      <summary>Vector path & viewport</summary>
      <p className="inspector-note">
        Edit SVG path commands and their source coordinate system. The layer
        frame controls placement and output size. Changes apply together.
      </p>
      <Control label="Vector path commands">
        <textarea
          rows={6}
          maxLength={200000}
          value={draft.path}
          onChange={(e) => setDraft({ ...draft, path: e.target.value })}
        />
      </Control>
      <Toggle
        label="Custom vector viewport"
        value={draft.custom}
        onChange={(custom) => setDraft({ ...draft, custom })}
      />
      {draft.custom ? (
        <>
          <p className="inspector-note">
            The viewport maps this source rectangle to the layer frame. Choose Y
            upward for PDF-style paths; Y downward follows SVG. Disabling the
            viewport restores a zero origin and downward Y.
          </p>
          <div className="control-grid">
            {(["x", "y", "width", "height"] as const).map((key) => (
              <Control key={key} label={`Vector viewport ${key}`}>
                <input
                  type="number"
                  step="any"
                  min={
                    key === "width" || key === "height" ? 0.000001 : -1000000
                  }
                  max={1000000}
                  value={draft.viewport[key]}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      viewport: {
                        ...draft.viewport,
                        [key]: Number(e.target.value),
                      },
                    })
                  }
                />
              </Control>
            ))}
          </div>
          <Control label="Vector Y direction">
            <Select
              value={draft.viewport.yAxis}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  viewport: {
                    ...draft.viewport,
                    yAxis: e.target.value as "down" | "up",
                  },
                })
              }
            >
              <option value="down">Downward (SVG)</option>
              <option value="up">Upward (PDF)</option>
            </Select>
          </Control>
        </>
      ) : (
        <div className="control-grid">
          {(["width", "height"] as const).map((key) => (
            <Control key={key} label={`Vector source ${key}`}>
              <input
                type="number"
                step="any"
                min={0.000001}
                max={14400}
                value={draft[key]}
                onChange={(e) =>
                  setDraft({ ...draft, [key]: Number(e.target.value) })
                }
              />
            </Control>
          ))}
        </div>
      )}
      {!validation.success && (
        <p role="alert">{validation.error.issues[0]?.message}</p>
      )}
      <Button disabled={!validation.success} onClick={() => onChange(patch)}>
        Apply vector path
      </Button>
      <Button onClick={() => setDraft(saved)}>Reset vector draft</Button>
      <Control label="Vector fill rule">
        <Select
          value={node.fillRule}
          onChange={(e) =>
            onChange({ fillRule: e.target.value as "nonzero" | "evenodd" })
          }
        >
          <option value="nonzero">Nonzero</option>
          <option value="evenodd">Even-odd</option>
        </Select>
      </Control>
    </details>
  );
}
